<template>
  <section class="products-section flex-col">
    <h2>{{ heading }}</h2>
    <p class="subHeading">{{ subHeading }}</p>

    <div class="content testimonial-swiper" role="region" :aria-label="`${heading} Carousel`">
      <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {{ activeSlideAnnouncement }}
      </div>
      <Swiper :modules="modules" :slides-per-view="1" :space-between="16" :centeredSlides="testimonial" :loop="true"
        :autoplay="autoplayOptions" :keyboard="{ enabled: true, onlyInViewport: true }"
        @swiper="onSwiper" @slideChange="onSlideChange" @focusin="handleFocusIn" @focusout="handleFocusOut"
        :effect="testimonial ? 'coverflow' : 'slide'" :coverflowEffect="testimonial ? coverflowEffect : {}"
        :breakpoints="{
          0: { slidesPerView: 1, spaceBetween: 16 },
          640: { slidesPerView: 1, spaceBetween: 20 },
          1024: { slidesPerView: 3, spaceBetween: 30 },
        }">
        <!-- 🦴 Skeleton Loader Slides -->
        <SwiperSlide v-if="isLoading" v-for="n in 5" :key="'skeleton-' + n" class="testimonial-slide"
          aria-hidden="true">
          <div class="skeleton-card">
            <div class="skeleton-image"></div>
            <div class="skeleton-text title"></div>
            <div class="skeleton-text subtitle"></div>
          </div>
        </SwiperSlide>

        <!-- Real Data Slides -->
        <SwiperSlide v-else v-for="(item, index) in items" :key="index" class="testimonial-slide">
          <component :is="CardComponent" v-bind="item" />
        </SwiperSlide>

      </Swiper>

      <div v-if="showCarouselControls" class="carousel-controls" aria-label="Carousel controls">
        <button class="nav-btn prev" aria-label="Previous slide" @click="swiperInstance?.slidePrev()">
          <v-icon aria-hidden="true">mdi-chevron-left</v-icon>
        </button>
        <button
          class="nav-btn play-pause"
          :aria-label="isAutoplayPaused ? 'Play carousel' : 'Pause carousel'"
          :aria-pressed="isAutoplayPaused"
          @click="toggleAutoplay"
        >
          <v-icon aria-hidden="true">{{ isAutoplayPaused ? "mdi-play" : "mdi-pause" }}</v-icon>
        </button>
        <button class="nav-btn next" aria-label="Next slide" @click="swiperInstance?.slideNext()">
          <v-icon aria-hidden="true">mdi-chevron-right</v-icon>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { Swiper, SwiperSlide } from "swiper/vue";
import {
  Navigation,
  Pagination,
  Autoplay,
  EffectCoverflow,
  A11y,
  Keyboard,
} from "swiper/modules";
import { computed, ref } from "vue";
import "swiper/css/a11y";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";

const props = defineProps({
  heading: { type: String, required: true },
  subHeading: { type: String, default: "" },
  items: { type: Array, default: () => [] },
  CardComponent: { type: [Object, Function], required: true },
  testimonial: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false }, // 👈 New prop
});

const modules = [Navigation, Pagination, Autoplay, A11y, Keyboard];
if (props.testimonial) modules.push(EffectCoverflow);

const swiperInstance = ref(null);
const isAutoplayPaused = ref(false);
const activeSlideAnnouncement = ref("");
const showCarouselControls = computed(() => props.isLoading || props.items.length > 1);
const autoplayOptions = {
  delay: 3000,
  disableOnInteraction: false,
  pauseOnMouseEnter: true,
};

const onSwiper = (swiper) => {
  swiperInstance.value = swiper;
};

const onSlideChange = (swiper) => {
  if (swiper && props.items && props.items.length > 0) {
    const currentIdx = (swiper.realIndex ?? swiper.activeIndex ?? 0) + 1;
    activeSlideAnnouncement.value = `Slide ${currentIdx} of ${props.items.length}`;
  }
};

const toggleAutoplay = () => {
  if (!swiperInstance.value?.autoplay) return;

  if (isAutoplayPaused.value) {
    swiperInstance.value.autoplay.start();
  } else {
    swiperInstance.value.autoplay.stop();
  }

  isAutoplayPaused.value = !isAutoplayPaused.value;
};

const handleFocusIn = (e) => {
  if (swiperInstance.value) {
    if (swiperInstance.value.autoplay && !isAutoplayPaused.value) {
      swiperInstance.value.autoplay.pause();
    }

    // Prevent native browser scroll from distorting Swiper's CSS transform
    setTimeout(() => {
      if (swiperInstance.value.wrapperEl) {
        swiperInstance.value.wrapperEl.scrollLeft = 0;
        swiperInstance.value.wrapperEl.scrollTop = 0;
      }
      if (swiperInstance.value.el) {
        swiperInstance.value.el.scrollLeft = 0;
        swiperInstance.value.el.scrollTop = 0;
      }
      const contentEl = swiperInstance.value.el?.closest('.content');
      if (contentEl) {
        contentEl.scrollLeft = 0;
        contentEl.scrollTop = 0;
      }
    }, 0);

    // Tell Swiper to slide to the newly focused element organically
    if (e.target) {
      const slide = e.target.closest('.swiper-slide');
      if (slide) {
        const index = Array.from(slide.parentNode.children).indexOf(slide);
        if (index !== -1) {
          swiperInstance.value.slideTo(index);
        }
      }
    }
  }
};

const handleFocusOut = () => {
  if (swiperInstance.value && swiperInstance.value.autoplay && !isAutoplayPaused.value) {
    swiperInstance.value.autoplay.resume();
  }
};

const coverflowEffect = {
  rotate: 40,
  stretch: 0,
  depth: 100,
  modifier: 1,
  slideShadows: false,
};
</script>

<style scoped>
.products-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.content {
  margin-top: 30px;
  width: 96%;
  position: relative;
  overflow: hidden;
}

.testimonial-slide {
  width: 300px;
  display: flex;
  justify-content: center;
}

/* 🦴 Skeleton Styles */
.skeleton-card {
  width: 100%;
  max-width: 280px;
  background-color: #fff;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.skeleton-image {
  width: 100%;
  height: 180px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-text {
  height: 14px;
  width: 80%;
  margin-top: 12px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-text.title {
  width: 60%;
  height: 16px;
}

.skeleton-text.subtitle {
  width: 40%;
  height: 14px;
}

.carousel-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  z-index: 20;
}

.nav-btn {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border: 1px solid rgba(0, 39, 175, 0.18);
  border-radius: 50%;
  background-color: var(--cardBg, #fff);
  color: var(--buttonBg, #0027af);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 39, 175, 0.08);
}

.nav-btn :deep(.v-icon) {
  font-size: 22px;
}

.nav-btn:hover,
.nav-btn:focus-visible {
  background-color: var(--buttonBg, #0027af);
  border-color: var(--buttonBg, #0027af);
  color: var(--buttonText, #fff);
  box-shadow: 0 4px 10px rgba(0, 39, 175, 0.18);
  transform: translateY(-1px);
}

.nav-btn:focus-visible {
  outline: 2px solid var(--buttonBg, #0027af);
  outline-offset: 2px;
}

.play-pause {
  width: 40px;
  height: 40px;
}

@keyframes shimmer {
  0% {
    background-position: -400px 0;
  }

  100% {
    background-position: 400px 0;
  }
}

:deep(.swiper) {
  padding-bottom: 40px;
  overflow: visible;
}

@media (min-width: 1025px) {
  .content {
    width: 80%;
  }
}
</style>
